import { Globe, HeartHandshake, Maximize2 } from "lucide-react";
import { APG23_DONATE_URL, APG23_SITE_URL, IMAGES, PRESS_CARLINO_URL, PRESS_MAGAZINE_URL, STORY_CARS, TIMELINE } from "../../data/content";
import { StoryDocument } from "./StoryDocument";
import { InstagramBanner } from "./InstagramBanner";
import { Lightbox, useLightbox } from "./Lightbox";

const STORY_PHOTOS = [
  { src: IMAGES.finnicoAmici, label: "Finnico Corse — la squadra davanti alla sede della Comunità Papa Giovanni XXIII" },
  { src: IMAGES.restoDelCarlino, label: "Il Resto del Carlino, 16 settembre 2020 — A Savignano nasce il Legend Team" },
];

export const Timeline = () => {
  const lb = useLightbox();
  return (
  <>
  <section
    id="capitolo-1"
    data-testid="timeline-section"
    className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-b border-neutral-800"
  >
    <div className="mb-12">
      <h2 className="font-display text-3xl sm:text-4xl font-black uppercase text-white">
        Cronologia & Radici
      </h2>
      <p className="text-sm text-neutral-400 mt-1">
        Da Savignano sul Rubicone a Sant'Agata Feltria: la storia documentata di Davide
        "Il Finnico" Bolognesi, di Finnico Corse e del Legend Team.
      </p>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5 space-y-8">
        <div data-testid="story-intro" className="space-y-4 text-sm text-neutral-300 leading-relaxed">
          <p>
            Il soprannome <span className="text-white font-bold">"Il Finnico"</span> evoca la
            scuola rallistica del Nord Europa: lettura del fondo, freddezza e controllo. Nel
            2003 diventa la firma di una scuderia, <span className="text-white font-bold">Finnico
            Corse Motorsport</span>, che porta al via equipaggi e vetture differenti tra il 2003 e il 2012.
          </p>
          <p>
            Il <span className="text-white font-bold">Legend Team</span> ne eredita carattere ed
            esperienza e amplia lo sguardo: gara, formazione, eventi, accoglienza e comunicazione.
            Oggi si presenta come Motorsport Club e Scuola Guida, con sede in Strada Perticara a
            Sant'Agata Feltria, e guarda al futuro con Legend Village e Legend Magazine.
          </p>
        </div>

        <div data-testid="story-cars" className="border border-neutral-800 divide-y divide-neutral-800">
          <div className="mono-label text-xs text-neutral-500 uppercase tracking-wider px-4 py-2 bg-neutral-900">
            Vetture documentate &bull; archivi eWRC
          </div>
          {STORY_CARS.map((c) => (
            <div key={c.year} className="grid grid-cols-[4rem_1fr] gap-3 px-4 py-3">
              <span className="mono-label text-xs text-red-500 font-bold">{c.year}</span>
              <div>
                <div className="text-sm font-bold text-white uppercase">{c.car}</div>
                <div className="text-xs text-neutral-500">{c.note}</div>
              </div>
            </div>
          ))}
        </div>

        <div data-testid="story-logos" className="border border-neutral-800 bg-neutral-900">
          <div className="mono-label text-xs text-neutral-500 uppercase tracking-wider px-4 py-2 border-b border-neutral-800">
            Loghi storici
          </div>
          <div className="flex items-center gap-5 px-4 py-5">
            <div className="shrink-0 bg-neutral-950 border border-neutral-800 px-4 py-3">
              <img
                data-testid="finnico-corse-logo"
                src={IMAGES.finnicoLogo}
                alt="Logo storico Finnico Corse Motorsport"
                className="h-10 w-auto object-contain"
                loading="lazy"
              />
            </div>
            <div>
              <div className="text-sm font-bold text-white uppercase">Finnico Corse Motorsport</div>
              <div className="text-xs text-neutral-500 mt-0.5">
                Dal 2003 &bull; Massa Marittima &bull; la scuderia da cui nasce il Legend Team
              </div>
            </div>
          </div>
        </div>

        <StoryDocument />

        <figure data-testid="story-photo" className="group relative border border-neutral-800 overflow-hidden">
          <button
            type="button"
            data-testid="story-photo-open"
            onClick={() => lb.open(0)}
            aria-label="Ingrandisci la foto Finnico Corse"
            className="block w-full cursor-zoom-in"
          >
            <img
              src={IMAGES.finnicoAmici}
              alt={STORY_PHOTOS[0].label}
              className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
            <span className="absolute top-2 right-2 w-8 h-8 bg-neutral-950/80 border border-neutral-700 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
              <Maximize2 className="w-4 h-4" />
            </span>
          </button>
          <figcaption className="absolute inset-x-0 bottom-0 p-3 bg-neutral-950/85 flex flex-wrap items-center justify-between gap-2">
            <span className="mono-label text-[11px] uppercase tracking-wider text-neutral-300">
              Finnico Corse &bull; la squadra davanti alla sede della Papa Giovanni XXIII
            </span>
            <span className="flex items-center gap-2">
            <a
              data-testid="apg23-donate-link"
              href={APG23_DONATE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-600 hover:bg-red-500 text-white mono-label text-[10px] font-bold uppercase tracking-wider transition-colors"
            >
              <HeartHandshake className="w-3.5 h-3.5" /> Dona ora
            </a>
            <a
              data-testid="apg23-site-link"
              href={APG23_SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1 border border-neutral-600 hover:border-red-600 text-neutral-200 hover:text-red-500 mono-label text-[10px] font-bold uppercase tracking-wider transition-colors"
            >
              <Globe className="w-3.5 h-3.5" /> apg23.org
            </a>
            </span>
          </figcaption>
        </figure>
      </div>

      <div className="lg:col-span-7 flex flex-col">
      <ol data-testid="timeline-list" className="relative">
        <span className="absolute left-[8rem] sm:left-[10rem] top-2 bottom-2 w-px bg-neutral-800" aria-hidden />
        {TIMELINE.map((s, i) => (
          <li
            key={s.period}
            data-testid={`timeline-step-${i + 1}`}
            className="grid grid-cols-[8rem_1fr] sm:grid-cols-[10rem_1fr] gap-6 py-5 border-b border-neutral-900 last:border-0"
          >
            <div className="relative mono-label text-xs sm:text-sm font-bold uppercase tracking-wider text-neutral-300 text-right pr-6">
              {s.period}
              <span
                className={`absolute -right-[6px] top-1 w-[11px] h-[11px] ${
                  s.accent ? "bg-red-600" : "bg-neutral-700"
                }`}
              />
            </div>
            <p className="text-sm text-neutral-300 leading-relaxed">{s.text}</p>
          </li>
        ))}
      </ol>
      <div className="mt-auto pt-8 space-y-6">
        <a
          data-testid="press-magazine"
          href={PRESS_MAGAZINE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex flex-col sm:flex-row items-stretch border border-neutral-800 hover:border-red-600 bg-neutral-900 transition-colors"
        >
          <span className="relative shrink-0 sm:w-56 overflow-hidden border-b sm:border-b-0 sm:border-r border-neutral-800">
            <img
              src={IMAGES.articoloFinnico}
              alt="Il Finnico al volante della Lancia Delta Jolly Club n.3 — anteprima articolo Legend Magazine"
              className="w-full h-40 sm:h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
            <img src={IMAGES.logoMagazine} alt="" aria-hidden className="absolute left-2 bottom-2 h-6 w-auto bg-white/95 px-1.5 py-0.5" />
          </span>
          <span className="p-5 flex flex-col justify-between gap-3 min-w-0">
            <span>
              <span className="mono-label text-xs text-red-500 uppercase tracking-wider block">Legend Magazine &bull; Approfondimento</span>
              <span className="text-sm font-bold text-white uppercase mt-1 block">Storia del Finnico e nascita di Legend Magazine</span>
              <span className="text-xs text-neutral-400 mt-1 block">
                Dalle prime gare con Finnico Corse (2003) alla testata giornalistica: il racconto completo di Davide Bolognesi.
              </span>
            </span>
            <span className="mono-label text-[11px] font-bold uppercase tracking-widest text-red-500 group-hover:text-red-400">
              Leggi su legendmagazine.it &rarr;
            </span>
          </span>
        </a>
        <figure data-testid="press-carlino" className="border border-neutral-800 bg-neutral-900 flex flex-col sm:flex-row items-stretch">
          <button
            type="button"
            data-testid="press-carlino-open"
            onClick={() => lb.open(1)}
            aria-label="Ingrandisci il ritaglio del Resto del Carlino"
            className="group relative shrink-0 sm:w-56 cursor-zoom-in overflow-hidden border-b sm:border-b-0 sm:border-r border-neutral-800"
          >
            <img
              src={IMAGES.restoDelCarlino}
              alt="Il Resto del Carlino, 16 settembre 2020: A Savignano nasce il Legend Team"
              className="w-full h-40 sm:h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
            <span className="absolute top-2 right-2 w-8 h-8 bg-neutral-950/80 border border-neutral-700 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
              <Maximize2 className="w-4 h-4" />
            </span>
          </button>
          <figcaption className="p-5 flex flex-col justify-between gap-4 min-w-0">
            <div>
              <div className="mono-label text-xs text-red-500 uppercase tracking-wider">Rassegna stampa &bull; 2020</div>
              <div className="text-sm font-bold text-white uppercase mt-1">"A Savignano nasce il Legend Team"</div>
              <div className="text-xs text-neutral-400 mt-1">
                Il Resto del Carlino, mercoledì 16 settembre 2020 — l'inaugurazione con le auto da rally in parata.
                Clicca la foto per ingrandire il ritaglio.
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                data-testid="press-carlino-zoom"
                onClick={() => lb.open(1)}
                className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white text-xs font-black uppercase tracking-widest transition-colors"
              >
                Ingrandisci
              </button>
              <a
                data-testid="press-carlino-link"
                href={PRESS_CARLINO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 border border-neutral-700 hover:border-red-600 text-xs font-bold uppercase tracking-widest text-neutral-200 hover:text-red-500 transition-colors"
              >
                Leggi l'articolo &rarr;
              </a>
            </div>
          </figcaption>
        </figure>
        <InstagramBanner />
      </div>
      </div>
    </div>
  </section>
  <Lightbox items={STORY_PHOTOS} index={lb.index} onClose={lb.close} onChange={lb.change} />
  </>
  );
};
